import { alpha, Box, useTheme } from '@chronosphereio/chrono-ui';
import { scaleLinear } from '@chronosphereio/core';
import { useMemo, useState } from 'react';
import { DependencyMapLink } from './DependencyMapLink';
import { DEPENDENCY_MAP_PAN_BUTTON_CLASS, DependencyMapPanButton } from './DependencyMapNav';
import { DependencyMapNode, LABEL_FONT_SIZE, LABEL_OFFSET_X } from './DependencyMapNode';
import { colorForStepIdx, extremaStepIdxForLink, useDependencyMapConfig } from './dependency-map-config';
import { AGGREGATE_LINK_STROKE_PX, LINK_WIDTH_MAX_PX, LINK_WIDTH_MIN_PX } from './link-style';
import { getMeasureCtx } from './measure-text-ctx';
import { DependencyLink, DependencyNode, isAggregateLink } from './types';
import { usePanController } from './use-pan-controller';

// Trailing buffer past the longest right-most label at max right-pan.
const LABEL_TRAILING_PAD_PX = 12;

const PAN_ANIMATION_MS = 300;

const EDGE_FADE_WIDTH_PX = 10;

// Floor for cluster height so sparse clusters don't read as a sliver.
const MIN_RENDER_HEIGHT_PX = 100;

/**
 * Measures a label's rendered width using a detached canvas 2D context.
 * SVG `<text>` doesn't set a `fontFamily` so it inherits whatever the page
 * provides; `sans-serif` is a safe approximation and the trailing pad below
 * absorbs any small mismatch.
 */
const measureLabelWidth = (ctx: CanvasRenderingContext2D, label: string): number => ctx.measureText(label).width;

interface DependencyMapClusterProps {
  /** Width of the graph area; provided by the parent's resize observer. */
  width: number;
  /** Height of the graph area; doubles as the SVG-available space. */
  height: number;
  /**
   * What to render. Caller pre-computes the focused subset (1-hop neighborhood
   * via `layoutFocusedSubset`) when applicable; this component just renders
   * whatever it's given.
   */
  nodes: DependencyNode[];
  links: DependencyLink[];
  /** Per-node self-traffic values (`from === to` in the source data); forwarded to `DependencyMapNode`. */
  selfLoops: ReadonlyMap<string, number>;
  /** Focused node id, or null for the full view. Fully controlled by the parent. */
  focusedId: string | null;
  /** Clicking the focused node sends `null` to clear focus. */
  onFocusedIdChange: (id: string | null) => void;
}

// Unified hover state. Both kinds drive `connectedIds` below.
type Hover = { kind: 'node'; id: string } | { kind: 'link'; from: string; to: string };

export const DependencyMapCluster = ({
  width,
  height,
  nodes,
  links,
  selfLoops,
  focusedId,
  onFocusedIdChange,
}: DependencyMapClusterProps) => {
  const { theme } = useTheme();
  const canvasBg = theme.palette.background.canvas;
  const containerBg = theme.palette.background.container;
  const labelColor = theme.palette.text.primary;

  const { layout, thresholds } = useDependencyMapConfig();

  // One column per pan click keeps the grid visually stable across pans.
  const panStepPx = layout.columnSpacingPx;

  const nodesById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  // Layout centers on the origin so `minNodeX` is typically negative. Used to
  // pin the leftmost column to `LEFT_PADDING_PX` and bound right-pan.
  const { minNodeX, maxNodeX } = useMemo(() => {
    if (nodes.length === 0) return { minNodeX: 0, maxNodeX: 0 };

    const xs = nodes.map((n) => n.x);

    return { minNodeX: Math.min(...xs), maxNodeX: Math.max(...xs) };
  }, [nodes]);

  const graphWidth = maxNodeX - minNodeX;

  // Sized to the tallest column. `count * spacing` (vs `(count - 1) * spacing`)
  // gives half a spacing-unit of natural padding above and below.
  const maxColumnSize = useMemo(() => {
    if (nodes.length === 0) return 0;

    const perColumn = new Map<number, number>();
    nodes.forEach((n) => perColumn.set(n.x, (perColumn.get(n.x) ?? 0) + 1));

    return Math.max(...perColumn.values());
  }, [nodes]);

  // `renderHeight` floor stops sparse layouts from rendering as a sliver, but
  // is capped at `height` so we never overflow a container smaller than the floor.
  const svgHeight = maxColumnSize * layout.nodeSpacingPx;
  const renderHeight = Math.max(svgHeight, Math.min(MIN_RENDER_HEIGHT_PX, height));

  // Computed from the widest right-most label. Other columns' labels overflow
  // into the next column's spacing, already accounted for by `columnSpacingPx`.
  const rightPadding = useMemo(() => {
    const ctx = getMeasureCtx();

    if (!ctx) return LABEL_OFFSET_X + LABEL_TRAILING_PAD_PX;

    ctx.font = `${LABEL_FONT_SIZE}px sans-serif`;
    const rightmost = nodes.filter((n) => n.x === maxNodeX);
    const maxLabelWidth =
      rightmost.length === 0 ? 0 : Math.max(...rightmost.map((n) => measureLabelWidth(ctx, n.label ?? n.id)));

    return LABEL_OFFSET_X + maxLabelWidth + LABEL_TRAILING_PAD_PX;
  }, [nodes, maxNodeX]);

  // Computed once per link and reused for ordering, node coloring, and
  // rendering, so the threshold matcher only runs once per link per render.
  const extremaByLink = useMemo(() => {
    const map = new Map<DependencyLink, { min: number; max: number }>();
    links.forEach((link) => map.set(link, extremaStepIdxForLink(link, thresholds)));
    return map;
  }, [links, thresholds]);

  // Sort by max-matched step ascending so problem traffic is drawn last
  // (wins z-order). Particularly important for 2-cycle back-edges, which
  // trace the same Bézier as their counterpart.
  const orderedLinks = useMemo(
    () => [...links].sort((a, b) => (extremaByLink.get(a)?.max ?? -1) - (extremaByLink.get(b)?.max ?? -1)),
    [links, extremaByLink]
  );

  // Built from regular links only — aggregates use a fixed stripe width and
  // shouldn't skew the scale. Collapse to the midpoint when there's no usable
  // range (no regular links, or all the same value), since scaleLinear returns
  // 0 in that case.
  const scaleLinkWidth = useMemo(() => {
    const regularValues = links.filter((l) => !isAggregateLink(l)).map((l) => l.value);
    const mid = (LINK_WIDTH_MIN_PX + LINK_WIDTH_MAX_PX) / 2;

    if (regularValues.length === 0) return () => mid;

    const min = Math.min(...regularValues);
    const max = Math.max(...regularValues);

    if (min === max) return () => mid;

    return scaleLinear({ min, max }, { min: LINK_WIDTH_MIN_PX, max: LINK_WIDTH_MAX_PX });
  }, [links]);

  // Each node picks up the highest-priority step among its connected links so
  // edges blend into their endpoints rather than ending in a contrasting dot.
  // Aggregate edges contribute their members' max so a node touching a bucket
  // with a Critical member still shows the Critical color.
  const nodeStepIdx = useMemo(() => {
    const map = new Map<string, number>();

    links.forEach((link) => {
      const idx = extremaByLink.get(link)?.max ?? -1;

      if (idx < 0) return;

      map.set(link.from, Math.max(map.get(link.from) ?? -1, idx));
      map.set(link.to, Math.max(map.get(link.to) ?? -1, idx));
    });

    return map;
  }, [links, extremaByLink]);

  const colorForNode = (id: string): string => colorForStepIdx(nodeStepIdx.get(id) ?? -1, thresholds);

  const [hover, setHover] = useState<Hover | null>(null);

  // Ids related to the current hover (node + 1-hop neighbors, or a link's
  // endpoints). Null when nothing's hovered, which short-circuits dimming.
  //
  // Also returns null when the hovered target no longer matches a visible
  // node — happens after a click swaps the layout (e.g. drill-in) and
  // unmounts the hovered `<g>` before its mouseleave can fire, which would
  // otherwise leave the new view dimmed against a ghost id.
  const connectedIds = useMemo(() => {
    if (hover === null) return null;

    if (hover.kind === 'node') {
      if (!nodesById.has(hover.id)) return null;

      const set = new Set<string>([hover.id]);
      links.forEach(({ from, to }) => {
        if (from === hover.id) set.add(to);
        if (to === hover.id) set.add(from);
      });

      return set;
    }

    if (!nodesById.has(hover.from) || !nodesById.has(hover.to)) return null;

    return new Set<string>([hover.from, hover.to]);
  }, [hover, links, nodesById]);

  const handleNodeEnter = (id: string) => setHover({ kind: 'node', id });
  const handleNodeLeave = (id: string) => setHover((prev) => (prev?.kind === 'node' && prev.id === id ? null : prev));
  const handleLinkEnter = (link: DependencyLink) => setHover({ kind: 'link', from: link.from, to: link.to });
  const handleLinkLeave = (link: DependencyLink) =>
    setHover((prev) => (prev?.kind === 'link' && prev.from === link.from && prev.to === link.to ? null : prev));

  const handleNodeClick = (id: string) => {
    if (nodesById.get(id)?.isAggregate) return;
    onFocusedIdChange(focusedId === id ? null : id);
  };

  const { translateX, canGoLeft, canGoRight, handleLeft, handleRight } = usePanController({
    focusedId,
    width,
    graphWidth,
    rightPadding,
    minNodeX,
    panStepPx,
  });

  // Pre-measurement (parent's resize observer hasn't fired yet) — bail out
  // rather than render a degenerate 0×0 SVG. The parent re-renders us with
  // real dimensions on the next commit.
  if (width <= 0 || height <= 0) return null;

  // Content y is symmetric around 0; translating by half the render height
  // centers the graph (also inside any `MIN_RENDER_HEIGHT_PX` padded frame).
  const translateY = renderHeight / 2;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        width,
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        // Hide the pan rail at rest; reveal on hover/focus so the rail's tint
        // and the chevron fade in together. `focus-within` covers keyboard
        // users tabbing through the buttons.
        [`& .${DEPENDENCY_MAP_PAN_BUTTON_CLASS}`]: {
          opacity: 0,
          transition: 'opacity 150ms ease-out',
        },
        [`&:hover .${DEPENDENCY_MAP_PAN_BUTTON_CLASS}, &:focus-within .${DEPENDENCY_MAP_PAN_BUTTON_CLASS}`]: {
          opacity: 1,
        },
      }}
    >
      <svg width={width} height={renderHeight}>
        {/* CSS transform (not SVG attr) so `transition` can animate it. */}
        <g
          style={{
            transform: `translate(${translateX}px, ${translateY}px)`,
            transition: `transform ${PAN_ANIMATION_MS}ms ease-out`,
          }}
        >
          {orderedLinks.map((link) => {
            const from = nodesById.get(link.from);
            const to = nodesById.get(link.to);

            if (!from || !to) return null;

            const isAggregate = isAggregateLink(link);

            return (
              <DependencyMapLink
                key={`${link.from}-${link.to}`}
                link={link}
                from={from}
                to={to}
                connectedIds={connectedIds}
                strokeWidth={isAggregate ? AGGREGATE_LINK_STROKE_PX : scaleLinkWidth(link.value)}
                extrema={extremaByLink.get(link) ?? { min: -1, max: -1 }}
                onEnter={handleLinkEnter}
                onLeave={handleLinkLeave}
              />
            );
          })}
          {nodes.map((node) => (
            <DependencyMapNode
              key={node.id}
              node={node}
              color={colorForNode(node.id)}
              connected={connectedIds === null || connectedIds.has(node.id)}
              canvasBg={canvasBg}
              labelColor={labelColor}
              selfLoops={selfLoops}
              onEnter={handleNodeEnter}
              onLeave={handleNodeLeave}
              onClick={handleNodeClick}
            />
          ))}
        </g>
      </svg>
      {/* Cues the user there's more content off-screen; the chevrons sit on
       * top to make the cue actionable. Both only render when pan is
       * available. */}
      {canGoLeft && <EdgeFade side="left" color={containerBg} />}
      {canGoRight && <EdgeFade side="right" color={containerBg} />}
      {canGoLeft && <DependencyMapPanButton side="left" onClick={handleLeft} />}
      {canGoRight && <DependencyMapPanButton side="right" onClick={handleRight} />}
    </Box>
  );
};

interface EdgeFadeProps {
  side: 'left' | 'right';
  /** Solid color the gradient fades from (container background). */
  color: string;
}

/**
 * Vertical edge overlay cueing off-screen content. Non-interactive so it
 * doesn't swallow hover/click events on the SVG below.
 */
const EdgeFade = ({ side, color }: EdgeFadeProps) => {
  const gradientDirection = side === 'left' ? 'to right' : 'to left';
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        [side]: 0,
        width: EDGE_FADE_WIDTH_PX,
        background: `linear-gradient(${gradientDirection}, ${color}, ${alpha(color, 0)})`,
        pointerEvents: 'none',
      }}
    />
  );
};
